import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import StorefrontLayout from "../components/storefront/Layout";
import ProductCard from "../components/storefront/ProductCard";
import Breadcrumbs, { breadcrumbJsonLd } from "../components/storefront/Breadcrumbs";
import { api } from "../lib/api";
import { useSeo } from "../lib/seo";

/**
 * Renders SEO landing routes like:
 *   /:make
 *   /:make/:model
 *   /:make/:model/:category
 *   /:make/:model/:position  (left-side-mirror | right-side-mirror)
 *
 * If the URL doesn't match a known vehicle structure we redirect to /shop.
 */
const CATEGORY_SLUGS = ["side-mirror", "windshield"];
const POSITION_ALIAS = {
  "left-side-mirror": { category: "side-mirror", position: "left" },
  "right-side-mirror": { category: "side-mirror", position: "right" },
  "side-mirror-pair": { category: "side-mirror", position: "pair" },
};

export default function SEOLanding() {
  const { p1, p2, p3 } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let make = p1;
    let model = p2;
    let category = null;
    let position = null;

    // Handle position alias in p3
    if (p3 && POSITION_ALIAS[p3]) {
      const a = POSITION_ALIAS[p3];
      category = a.category;
      position = a.position;
    } else if (p3 && CATEGORY_SLUGS.includes(p3)) {
      category = p3;
    } else if (p3) {
      // unknown third segment — treat as not found
      setNotFound(true);
      return;
    }
    // If p2 is itself a category (e.g., /ferrari/side-mirror), shift
    if (!p3 && p2 && CATEGORY_SLUGS.includes(p2)) {
      category = p2;
      model = null;
    }

    const params = new URLSearchParams({ make });
    if (model) params.set("model", model);
    if (category) params.set("category", category);
    if (position) params.set("position", position);

    api.get(`/seo/landing?${params.toString()}`)
      .then(async ({ data }) => {
        // Overlay per-path seo_pages override (Phase 1.5)
        const path = "/" + [p1, p2, p3].filter(Boolean).join("/");
        let override = null;
        try {
          const r = await api.get(`/seo/page?path=${encodeURIComponent(path)}`);
          override = r.data;
        } catch { /* no override — fall back to defaults */ }
        setData({ ...data, _meta: { make, model, category, position, path }, _override: override });
      })
      .catch(() => setNotFound(true));
  }, [p1, p2, p3]);

  const { brand, model, category, position, products } = data || {};
  const override = data?._override;
  const path = data?._meta?.path || "/";
  const positionLabel = position ? `${position.charAt(0).toUpperCase() + position.slice(1)} ` : "";
  const categoryLabel = category === "side-mirror" ? "Side Mirrors" : category === "windshield" ? "Windshields" : "";
  const titleParts = [brand?.name, model?.name, positionLabel + categoryLabel].filter(Boolean);
  const computedTitle = titleParts.join(" ") + " · Lecis Auto Glass";
  const computedDescription = `OEM-style and aftermarket ${positionLabel.toLowerCase()}${categoryLabel.toLowerCase()} for ${brand?.name || ""}${model ? " " + model.name : ""}. Fitment-verified, shipped nationwide from Fair Oaks, California.`;
  const title = override?.title || computedTitle;
  const description = override?.meta_description || computedDescription;
  const h1 = override?.h1 || `${positionLabel}${categoryLabel || (brand?.name ? brand.name + " parts" : "")}`;

  // Breadcrumb items
  const crumbs = [{ label: "Home", to: "/" }];
  if (brand) crumbs.push({ label: brand.name, to: `/${brand.slug}` });
  if (model) crumbs.push({ label: model.name, to: `/${brand.slug}/${model.slug}` });
  if (category) crumbs.push({ label: `${positionLabel}${categoryLabel}`.trim(), to: path });

  // JSON-LD
  const itemList = products && products.length ? {
    "@context": "https://schema.org",
    "@type": "ItemList",
    itemListElement: products.slice(0, 20).map((p, i) => ({
      "@type": "ListItem",
      position: i + 1,
      url: `${typeof window !== "undefined" ? window.location.origin : ""}/product/${p.slug}`,
      name: p.name,
    })),
  } : null;

  useSeo({
    title,
    description,
    canonicalPath: override?.canonical_url || path,
    robots: override?.is_indexable === false ? "noindex,follow" : "index,follow",
    og: { image: override?.og_image, type: "website" },
    jsonLd: {
      breadcrumb: data ? breadcrumbJsonLd(crumbs) : null,
      itemList,
      seoPage: override?.schema_jsonld || null,
    },
  });

  if (notFound) {
    return (
      <StorefrontLayout>
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-32 text-center" data-testid="seo-not-found">
          <h1 className="font-display text-5xl mb-4">404</h1>
          <p className="text-zinc-500 mb-8">We couldn't find this page.</p>
          <button onClick={() => navigate("/")} className="text-red-500 hover:text-red-400">Go home</button>
        </div>
      </StorefrontLayout>
    );
  }

  if (!data) {
    return <StorefrontLayout><div className="max-w-7xl mx-auto px-6 lg:px-12 py-32 text-zinc-500">Loading…</div></StorefrontLayout>;
  }

  return (
    <StorefrontLayout>
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16">
        <div className="text-[10px] uppercase tracking-[0.3em] text-red-500 font-bold mb-3">
          {[brand?.name, model?.name].filter(Boolean).join(" / ")}
        </div>
        <h1 className="font-display text-4xl md:text-6xl tracking-tighter font-medium mb-3" data-testid="seo-heading">
          {positionLabel}{categoryLabel || `${brand?.name} parts`}
        </h1>
        <p className="text-zinc-400 max-w-2xl text-base leading-relaxed mb-12">
          {description}
        </p>

        {/* Sub-routes */}
        {model && !category && (
          <div className="flex flex-wrap gap-3 mb-10">
            <Link to={`/${brand.slug}/${model.slug}/side-mirror`} className="text-xs uppercase tracking-[0.2em] font-bold border border-white/20 hover:border-red-500 hover:text-red-500 px-4 py-2 transition" data-testid="seo-sublink-side-mirror">Side mirrors</Link>
            <Link to={`/${brand.slug}/${model.slug}/windshield`} className="text-xs uppercase tracking-[0.2em] font-bold border border-white/20 hover:border-red-500 hover:text-red-500 px-4 py-2 transition" data-testid="seo-sublink-windshield">Windshields</Link>
            <Link to={`/${brand.slug}/${model.slug}/left-side-mirror`} className="text-xs uppercase tracking-[0.2em] font-bold border border-white/20 hover:border-red-500 hover:text-red-500 px-4 py-2 transition" data-testid="seo-sublink-left">Left mirror</Link>
            <Link to={`/${brand.slug}/${model.slug}/right-side-mirror`} className="text-xs uppercase tracking-[0.2em] font-bold border border-white/20 hover:border-red-500 hover:text-red-500 px-4 py-2 transition" data-testid="seo-sublink-right">Right mirror</Link>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="seo-product-grid">
          {products.map((p) => <ProductCard key={p.id} product={p} />)}
        </div>

        {products.length === 0 && (
          <div className="text-center text-zinc-500 py-20 border border-dashed border-white/10 rounded-md" data-testid="seo-no-products">
            No products listed for this fitment yet. We're constantly expanding our inventory — check back soon.
          </div>
        )}
      </div>
    </StorefrontLayout>
  );
}
